/**
 * To ensure all UI localizations are translated properly, this function checks
 * the existence of a given key. It throws an error for production builds if the
 * key does not exist. During development, it just returns the key.
 */
const translate = <T extends Record<string, string | null>>(
  translations: T,
  key: keyof T,
  replacements?: { [key: string]: string },
): string => {
  const translationVal = translations[key];

  if (!translationVal) {
    // Only throw error for production builds if we explicitly set
    // MEWA_CHECK_TRANSLATIONS. Regular production builds without the
    // MEWA_CHECK_TRANSLATIONS flag will return the key. This is useful during
    // development while the translations are still a work in progress.
    if (
      process.env['NODE_ENV'] === 'production' &&
      process.env['MEWA_CHECK_TRANSLATIONS'] === 'true'
    ) {
      throw new Error('Missing translation: ' + key.toString());
    }

    return key as string;
  }

  if (typeof replacements === 'object') {
    return Object.entries(replacements).reduce(
      (translated, [key, value]) => translated.replace(`{${key}}`, value),
      translationVal as string,
    );
  }

  // Cast to string as the typechecker does not seem to recognize that the
  // `translationVal` cannot be null at this point. If it is null, the function
  // returns the key or throws an exception when checking translations is
  // enabled.
  return translationVal as string;
};

export default translate;
